import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, MapPin, Tag } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSiteConfig } from "@/lib/siteConfig";

export default function Home() {
  const { config = {} } = useSiteConfig();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => {
        if (Array.isArray(r.data)) {
          setFeatured(r.data.slice(0, 4));
        } else {
          setFeatured([]);
        }
      })
      .catch((err) => {
        console.error("Errore nel recupero dei prodotti in evidenza:", err);
        setFeatured([]);
      });
  }, []);

  const safeConfig = config || {};
  const heroImg =
    safeConfig.hero_image ||
    "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=1400";
  const categories = Array.isArray(safeConfig.home_categories)
    ? safeConfig.home_categories
    : [];

  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-[var(--bs-border)] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--bs-red)] mb-6 flex items-center gap-2">
              <span className="w-8 h-px bg-[var(--bs-red)]"></span> Vendita stock & fallimenti
            </div>
            <h1
              data-testid="hero-title"
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter mb-6"
            >
              {safeConfig.hero_title || "Grandi Marche a Prezzi di Stock"}
            </h1>
            <p
              data-testid="hero-subtitle"
              className="text-base sm:text-lg text-[var(--bs-muted)] max-w-xl mb-8 leading-relaxed"
            >
              {safeConfig.hero_subtitle || "Offerte esclusive su arredamento, elettrodomestici e casalinghi."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/shop"
                data-testid="btn-hero-shop"
                className="bs-btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest"
              >
                Sfoglia il catalogo <ArrowRight size={16} />
              </Link>
              <Link
                to="/shop"
                data-testid="btn-hero-cat"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest border border-black hover:bg-black hover:text-white transition-colors"
              >
                Offerte del giorno
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] w-full overflow-hidden border border-black">
              <img
                src={heroImg}
                alt="Buti Stock"
                data-testid="hero-image"
                className="w-full h-full object-cover grayscale contrast-110"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[var(--bs-red)] text-white p-6 hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.3em] mb-1">Prenota online</div>
              <div className="font-display font-black text-3xl leading-none">RITIRO IN SEDE</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="border-b border-[var(--bs-border)] bg-[var(--bs-surface)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--bs-border)]">
          {[
            { icon: Tag, label: "Prezzi imbattibili", desc: "Superprezzi, sempre!" },
            { icon: MapPin, label: "Ritiro in sede", desc: "Dopo conferma WhatsApp" },
            { icon: Shield, label: "Azienda Italiana", desc: "Azienda 100% Italiana" },
            { icon: Zap, label: "Nuovi arrivi", desc: "Ogni settimana" },
          ].map((f, i) => (
            <div key={i} className="bg-white p-6 flex items-center gap-4">
              <f.icon size={28} className="text-[var(--bs-red)] shrink-0" />
              <div>
                <div className="font-display font-bold text-sm">{f.label}</div>
                <div className="text-xs text-[var(--bs-muted)]">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--bs-red)] mb-2">Reparti</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Cosa stai cercando?</h2>
            </div>
            <Link
              to="/shop"
              className="hidden sm:inline text-sm font-bold uppercase tracking-widest hover:text-[var(--bs-red)]"
              data-testid="link-all-categories"
            >
              Vedi tutto →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((c) => (
              <Link
                key={c.name}
                to={`/shop?category=${encodeURIComponent(c.name)}`}
                data-testid={`category-${c.name}`}
                className="group relative aspect-[4/5] overflow-hidden border border-black block"
              >
                <img
                  src={c.image}
                  alt={c.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="font-display font-black text-3xl tracking-tighter">{c.name}</div>
                  <div className="text-xs uppercase tracking-widest opacity-80 mt-1 flex items-center gap-2">
                    Sfoglia <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED PRODUCTS */}
      <section className="border-t border-[var(--bs-border)] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--bs-red)] mb-2">Offerte in evidenza</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Affari da non perdere</h2>
            </div>
            <Link
              to="/shop"
              className="text-sm font-bold uppercase tracking-widest hover:text-[var(--bs-red)]"
              data-testid="link-all-products"
            >
              Tutti i prodotti →
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {(Array.isArray(featured) ? featured : []).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* MERCATINO PROMO */}
      {safeConfig.used_market?.enabled && (
        <section className="border-t border-[var(--bs-border)] bg-[var(--bs-surface)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--bs-red)] mb-3">Novità</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-4">
                {safeConfig.used_market.title || "Mercatino dell'usato"}
              </h2>
              <p className="text-base text-[var(--bs-muted)] mb-6 max-w-lg leading-relaxed">
                {safeConfig.used_market.subtitle || "Prodotti rivisionati e non rivisionati, direttamente dal nostro magazzino."}
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="bg-blue-600 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5">
                  Rivisionato
                </span>
                <span className="bg-zinc-800 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5">
                  NON Rivisionato
                </span>
              </div>
              <Link
                to="/mercatino"
                data-testid="btn-home-mercatino"
                className="bs-btn-primary inline-flex items-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest"
              >
                Vai al mercatino <ArrowRight size={16} />
              </Link>
            </div>
            {safeConfig.used_market.hero_image && (
              <div className="aspect-[4/3] overflow-hidden border border-black">
                <img
                  src={safeConfig.used_market.hero_image}
                  alt="Mercatino"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-black text-white bs-grain">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-20 text-center">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--bs-red)] mb-4">Sei un&apos;azienda?</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-4">
            Compriamo il tuo stock.
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-8">
            Hai giacenze di magazzino, invenduto o fine serie? Ti facciamo una valutazione gratuita e ritiriamo tutto.
          </p>
          <a
            href="mailto:info@butistock.it"
            data-testid="btn-cta-contact"
            className="bs-btn-primary inline-flex items-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest"
          >
            Contattaci ora <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}